// 🔧 bundle_builder.js
// ✅ Purpose: Builds optimized runtime Content Bundles from published Content Items
// ✅ Features:
// - Converts editable Content Items into runtime Content Bundles
// - Groups content by section and element
// - Ignores unpublished content
// - Keeps runtime bundles small and optimized
// - Contains no Firestore logic
// - Contains no React logic
// - Fully unit-testable business logic
// - Uses the canonical Content Bundle model

import {
  createContentBundle,
} from "../models/content_bundle_model";

import {
  shouldIncludeContentItemInBundle,
} from "../models/content_item_model";

/**
 * Builds a runtime Content Bundle.
 *
 * Parameters:
 *
 * area
 *      Canonical page/content area.
 *
 * contentItems
 *      Array of published Content Items.
 *
 * bundleOptions
 *      scopeId
 *      locale
 *      profile
 *      version
 *      generatedAt
 *      generatedBy
 */
export function buildContentBundle({
  area,
  contentItems = [],
  bundleOptions = {},
}) {
  console.log(
    "[Content Engine] Building bundle:",
    area
  );

  const content = {};

  /**
   * ------------------------------------------------------------------------
   * Convert Content Items into runtime structure.
   * ------------------------------------------------------------------------
   */
  contentItems.forEach((item) => {
    if (!shouldIncludeContentItemInBundle(item)) {
      return;
    }

    const {
      section,
      element,
      value,
    } = item;

    if (!content[section]) {
      content[section] = {};
    }

    content[section][element] = value;
  });

  /**
   * ------------------------------------------------------------------------
   * Return canonical bundle.
   * ------------------------------------------------------------------------
   */
  return createContentBundle({
    area,

    ...bundleOptions,

    content,
  });
}

/**
 * Builds bundles for multiple areas.
 *
 * Input:
 *
 * {
 *    event_home: [...],
 *    dashboard: [...],
 * }
 *
 * Output:
 *
 * {
 *    event_home: bundle,
 *    dashboard: bundle,
 * }
 */
export function buildContentBundles({
  bundles = {},
  bundleOptions = {},
}) {
  const results = {};

  Object.entries(bundles).forEach(
    ([area, contentItems]) => {
      results[area] = buildContentBundle({
        area,
        contentItems,
        bundleOptions,
      });
    }
  );

  return results;
}

/**
 * Returns the number of runtime values
 * inside a bundle.
 *
 * Useful for diagnostics.
 */
export function countBundleValues(
  bundle = {}
) {
  let count = 0;

  Object.values(bundle.content ?? {}).forEach(
    (section) => {
      count += Object.keys(section).length;
    }
  );

  return count;
}

/**
 * Returns every section inside a bundle.
 *
 * Useful for diagnostics and unit tests.
 */
export function getBundleSectionNames(
  bundle = {}
) {
  return Object.keys(bundle.content ?? {});
}